import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  Package,
  Users,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Truck,
  Calendar,
  RefreshCw,
  ArrowRight,
  Factory,
  FileText,
  Bell,
  Mail,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalWorkOrders: 0,
    completedToday: 0,
    inProgress: 0,
    pendingDelivery: 0,
    upcomingDeadlines: [],
    lowStockItems: 0,
    totalInventoryValue: 0,
    totalEmployees: 0,
    totalCustomers: 0,
    recentQuotations: 0,
  });
  const [workOrdersByStatus, setWorkOrdersByStatus] = useState([]);
  const [productionTrend, setProductionTrend] = useState([]);
  const [deadlineAlerts, setDeadlineAlerts] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [
        workOrdersRes,
        inventoryRes,
        employeesRes,
        customersRes,
        quotationsRes,
      ] = await Promise.all([
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/wo-customers`),
        axios.get(`${API}/quotations`),
      ]);

      const workOrders = workOrdersRes.data;
      const inventory = inventoryRes.data;
      const employees = employeesRes.data;
      const customers = customersRes.data;
      const quotations = quotationsRes.data;

      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      // Work order status counts
      const statusCounts = {
        available: 0,
        in_progress: 0,
        completed: 0,
        delivered: 0,
      };
      
      workOrders.forEach(wo => {
        const status = wo.status || 'available';
        if (statusCounts.hasOwnProperty(status)) {
          statusCounts[status]++;
        }
      });

      // Upcoming deadlines (within next 3 days)
      const upcomingDeadlines = workOrders.filter(wo => {
        const deliveryDate = wo.header?.delivery_date;
        if (!deliveryDate) return false;
        const delivery = new Date(deliveryDate);
        const now = new Date();
        const diffDays = Math.ceil((delivery - now) / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 3 && wo.status !== 'delivered' && wo.status !== 'completed';
      }).map(wo => ({
        ...wo,
        daysLeft: Math.ceil((new Date(wo.header?.delivery_date) - new Date()) / (1000 * 60 * 60 * 24))
      })).sort((a, b) => a.daysLeft - b.daysLeft);

      // 1-day deadline alerts for notification
      const oneDayAlerts = workOrders.filter(wo => {
        const deliveryDate = wo.header?.delivery_date;
        if (!deliveryDate) return false;
        const delivery = new Date(deliveryDate);
        const now = new Date();
        const diffDays = Math.ceil((delivery - now) / (1000 * 60 * 60 * 24));
        return diffDays === 1 && wo.status !== 'delivered' && wo.status !== 'completed';
      });
      setDeadlineAlerts(oneDayAlerts);

      // Low stock count (items with status 'low_stock' or qty < 5)
      const lowStock = inventory.filter(item => 
        item.status === 'low_stock' || (item.qty && item.qty < 5)
      ).length;

      // Calculate inventory value
      const inventoryValue = inventory.reduce((sum, item) => {
        const price = item.paper_price || 0;
        const weight = item.reel_weight || 0;
        return sum + (price * weight);
      }, 0);

      // Recent quotations (last 7 days)
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const recentQuotes = quotations.filter(q => {
        const created = new Date(q.created_at);
        return created >= weekAgo;
      }).length;

      // Status distribution for pie chart
      setWorkOrdersByStatus([
        { name: 'Available', value: statusCounts.available, color: '#06B6D4' },
        { name: 'In Progress', value: statusCounts.in_progress, color: '#F59E0B' },
        { name: 'Completed', value: statusCounts.completed, color: '#10B981' },
        { name: 'Delivered', value: statusCounts.delivered, color: '#8B5CF6' },
      ].filter(item => item.value > 0));

      // Production trend (last 7 days - simulated from work orders)
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        const dayOrders = workOrders.filter(wo => {
          const created = wo.header?.work_order_creation_date || wo.created_at?.split('T')[0];
          return created === dateStr;
        }).length;
        last7Days.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          orders: dayOrders,
        });
      }
      setProductionTrend(last7Days);

      setStats({
        totalWorkOrders: workOrders.length,
        completedToday: statusCounts.completed,
        inProgress: statusCounts.in_progress,
        pendingDelivery: statusCounts.completed,
        upcomingDeadlines: upcomingDeadlines.slice(0, 5),
        lowStockItems: lowStock,
        totalInventoryValue: inventoryValue,
        totalEmployees: employees.length,
        totalCustomers: customers.length,
        recentQuotations: recentQuotes,
      });

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  // Demo Mode removed (cleaned up Feb 2026)

  // Send deadline reminder email
  const sendDeadlineReminder = async (workOrder) => {
    try {
      const response = await axios.post(`${API}/send-deadline-reminder`, {
        work_order_number: workOrder.header?.work_order_number,
        customer_name: workOrder.header?.customer_name,
        item_name: workOrder.header?.item_name,
        delivery_date: workOrder.header?.delivery_date,
        quantity: workOrder.header?.required_quantity,
        customer_email: workOrder.header?.customer_email || null,
      });
      
      if (response.data.success) {
        toast.success(`Reminder sent for ${workOrder.header?.work_order_number}`);
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder email");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <LayoutDashboard className="h-8 w-8 text-cyan-400" />
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Real-time overview of your manufacturing operations</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              disabled={refreshing}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Deadline Alerts Banner */}
        {deadlineAlerts.length > 0 && (
          <div className="bg-red-500/10 border-2 border-red-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-red-400 font-bold text-lg">⚠️ Deadline Alert - Due Tomorrow!</h3>
                <div className="mt-2 space-y-2">
                  {deadlineAlerts.map((wo, idx) => (
                    <div key={wo.id || idx} className="flex items-center justify-between bg-red-500/10 rounded p-2">
                      <div>
                        <span className="text-white font-medium">{wo.header?.work_order_number}</span>
                        <span className="text-gray-400 mx-2">•</span>
                        <span className="text-gray-300">{wo.header?.customer_name}</span>
                        <span className="text-gray-400 mx-2">•</span>
                        <span className="text-gray-400">{wo.header?.item_name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        onClick={() => sendDeadlineReminder(wo)}
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Send Reminder
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Package className="h-8 w-8 text-cyan-400" />
                <span className="text-3xl font-black text-white">{stats.totalWorkOrders}</span>
              </div>
              <p className="text-cyan-300 text-sm mt-2 font-medium">Total Work Orders</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-8 w-8 text-amber-400" />
                <span className="text-3xl font-black text-white">{stats.inProgress}</span>
              </div>
              <p className="text-amber-300 text-sm mt-2 font-medium">In Progress</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
                <span className="text-3xl font-black text-white">{stats.completedToday}</span>
              </div>
              <p className="text-green-300 text-sm mt-2 font-medium">Completed</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Truck className="h-8 w-8 text-purple-400" />
                <span className="text-3xl font-black text-white">{stats.pendingDelivery}</span>
              </div>
              <p className="text-purple-300 text-sm mt-2 font-medium">Pending Delivery</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500/20 to-red-600/10 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <span className="text-3xl font-black text-white">{stats.lowStockItems}</span>
              </div>
              <p className="text-red-300 text-sm mt-2 font-medium">Low Stock Items</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <DollarSign className="h-8 w-8 text-emerald-400" />
                <span className="text-2xl font-black text-white">₹{(stats.totalInventoryValue / 1000).toFixed(0)}K</span>
              </div>
              <p className="text-emerald-300 text-sm mt-2 font-medium">Inventory Value</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Work Order Status Pie Chart */}
          <Card className="bg-black/60 border border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="h-5 w-5 text-cyan-400" />
                Work Order Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={workOrdersByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {workOrdersByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      formatter={(value, name) => [value, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                {workOrdersByStatus.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-gray-400">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Production Trend Chart */}
          <Card className="bg-black/60 border border-gray-700 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-400" />
                Production Trend (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={productionTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                    <XAxis dataKey="date" stroke="#6B7280" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                    <YAxis stroke="#6B7280" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#06B6D4' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="orders"
                      stroke="#06B6D4"
                      strokeWidth={2}
                      fill="url(#colorOrders)"
                      name="Work Orders"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Deadlines */}
          <Card className="bg-black/60 border border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-400" />
                Upcoming Deadlines
              </CardTitle>
              <Link to="/workorder">
                <Button variant="ghost" size="sm" className="text-cyan-400 hover:text-cyan-300">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats.upcomingDeadlines.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No upcoming deadlines</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.upcomingDeadlines.map((wo, idx) => (
                    <div
                      key={wo.id || idx}
                      className={cn(
                        "p-3 rounded-lg border flex items-center justify-between",
                        wo.daysLeft === 0 ? "bg-red-500/10 border-red-500/30" :
                        wo.daysLeft === 1 ? "bg-amber-500/10 border-amber-500/30" :
                        "bg-gray-800/50 border-gray-700"
                      )}
                    >
                      <div>
                        <p className="text-white font-medium">{wo.header?.work_order_number}</p>
                        <p className="text-gray-400 text-sm">{wo.header?.customer_name}</p>
                        <p className="text-gray-500 text-xs">{wo.header?.item_name}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={cn(
                          "font-bold",
                          wo.daysLeft === 0 ? "bg-red-500" :
                          wo.daysLeft === 1 ? "bg-amber-500" :
                          "bg-cyan-500"
                        )}>
                          {wo.daysLeft === 0 ? "TODAY" : wo.daysLeft === 1 ? "TOMORROW" : `${wo.daysLeft} DAYS`}
                        </Badge>
                        <p className="text-gray-500 text-xs mt-1">{wo.header?.delivery_date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Links & Stats */}
          <Card className="bg-black/60 border border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Factory className="h-5 w-5 text-cyan-400" />
                Quick Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/workorder/customers" className="block">
                  <div className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-cyan-500/50 rounded-lg p-4 transition-all">
                    <Users className="h-6 w-6 text-cyan-400 mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.totalCustomers}</p>
                    <p className="text-gray-400 text-sm">Customers</p>
                  </div>
                </Link>
                
                <Link to="/workorder#employees" className="block">
                  <div className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-purple-500/50 rounded-lg p-4 transition-all">
                    <Users className="h-6 w-6 text-purple-400 mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.totalEmployees}</p>
                    <p className="text-gray-400 text-sm">Employees</p>
                  </div>
                </Link>

                <Link to="/workorder/quotation" className="block">
                  <div className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-emerald-500/50 rounded-lg p-4 transition-all">
                    <FileText className="h-6 w-6 text-emerald-400 mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.recentQuotations}</p>
                    <p className="text-gray-400 text-sm">Recent Quotes</p>
                  </div>
                </Link>

                <Link to="/store/low-stock" className="block">
                  <div className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-red-500/50 rounded-lg p-4 transition-all">
                    <AlertTriangle className="h-6 w-6 text-red-400 mb-2" />
                    <p className="text-2xl font-bold text-white">{stats.lowStockItems}</p>
                    <p className="text-gray-400 text-sm">Low Stock</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
